import { ExternalIdProvider } from 'src/__generated__/backend/graphql'
import { LoginUser, User } from 'src/models/User'
import useSWR, { SWRConfiguration } from 'swr'
import { myAuthentication } from '../backend'
import { fetcher } from '../client'
import { GET_CURRENT_COMPANY, getProfile } from '../endpoints'
import { useMasterData } from '../masterData'
import { CurrentCompany } from '../types/currentCompany'
import { TargetCompany } from '../types/targetCompany'
import { toLoginUser, toUser } from './converters'

const loginUserFetcher = async (
  url: string,
): Promise<CurrentCompany | undefined> => {
  try {
    const authenticationPromise = myAuthentication().catch((error) => {
      // Authentication query is supplemental (provider display), so
      // it must not invalidate a valid session when it fails transiently.
      console.warn('Failed to fetch authentication providers:', error)
      return undefined
    })
    const data = await fetcher(url)

    // Check if response is for admin user
    // Admin users should access as guest to preview content
    if ('currentUser' in data && (data as any).currentUser) return undefined

    // Handle company user response
    if (!data.currentCompany) return undefined
    const authentication = await authenticationPromise

    return {
      currentCompany: {
        ...data.currentCompany,
        isFacebookLogin: !!authentication?.authentication?.providers?.includes(
          ExternalIdProvider.Facebook,
        ),
      },
    }
  } catch (error) {
    // In case of error, return undefined to allow guest access
    console.warn('Failed to fetch current company:', error)
    return undefined
  }
}

const useCurrentCompany = (config?: Partial<SWRConfiguration>) =>
  useSWR<CurrentCompany | undefined>(
    GET_CURRENT_COMPANY,
    loginUserFetcher,
    config,
  )

type UseLoginUserReturn = {
  isValidating: boolean
  user: LoginUser | undefined
}
export const useLoginUser = (
  fetchConfig?: Partial<SWRConfiguration>,
): UseLoginUserReturn => {
  const { data, ...ccRes } = useCurrentCompany(fetchConfig)
  const { data: masterData, ...mdRes } = useMasterData(fetchConfig)
  return {
    isValidating: ccRes.isValidating || mdRes.isValidating,
    user: toLoginUser(data, masterData),
  }
}

const userFetcher = async (url: string): Promise<User> => {
  const data: TargetCompany = await fetcher(url)
  return toUser(data)
}

export const useUser = (id: number, config?: Partial<SWRConfiguration>) =>
  useSWR<User>(id ? () => getProfile(id) : null, userFetcher, config)

// Hook to check if current user is admin
export const useIsAdminUser = (): boolean => {
  const { data } = useSWR<any>(
    GET_CURRENT_COMPANY,
    async (url) => {
      try {
        const response = await fetcher(url)
        return response
      } catch (error) {
        return null
      }
    },
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
    },
  )

  // Check if response contains currentUser (admin user)
  if (data && 'currentUser' in data && data.currentUser) return true

  return false
}
